import { useNavigate } from 'react-router-dom';
import { Badge } from '@/components/ui/badge';
import { Button } from '@/components/ui/button';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from '@/components/ui/dialog';
import type { ProfileCompletionSummary } from '@/lib/profileCompletion';
import {
  getOnboardingSectionPath,
  type ProfileOnboardingDecision,
} from '@/lib/profileOnboarding';
import { WorklynxIcon } from '@/components/icons/WorklynxIcon';

interface ProfileCompletionGateProps {
  decision: ProfileOnboardingDecision;
  summary: ProfileCompletionSummary;
  isDeferring: boolean;
  deferError: Error | null;
  onDefer: () => Promise<unknown>;
}

const SECTION_LABELS = {
  personal: 'Personal details',
  emergency: 'Emergency contact',
  'work-payroll': 'Work, payroll and tax',
  documents: 'Required documents',
  availability: 'Weekly availability',
} as const;

export function ProfileCompletionGate({
  decision,
  summary,
  isDeferring,
  deferError,
  onDefer,
}: ProfileCompletionGateProps) {
  const navigate = useNavigate();
  const isPersonalGate = decision.state === 'REQUIRE_PERSONAL';
  const canDefer = decision.state === 'DEFERABLE';
  const sectionPath = getOnboardingSectionPath(decision.section);
  const visibleIssues = summary.issues.slice(0, 5);

  return (
    <Dialog open onOpenChange={() => undefined}>
      <DialogContent
        className="max-w-xl [&>button]:hidden"
        onEscapeKeyDown={(event) => event.preventDefault()}
        onInteractOutside={(event) => event.preventDefault()}
      >
        <DialogHeader className="border-b-0 pb-3">
          <div className="mb-3 flex items-start justify-between gap-4">
            <span className="flex h-11 w-11 items-center justify-center rounded-md bg-warning-soft text-warning">
              {isPersonalGate ? (
                <WorklynxIcon name="error" size={20} />
              ) : (
                <WorklynxIcon name="clipboard-check" size={20} />
              )}
            </span>
            <Badge variant={canDefer ? 'warning' : 'destructive'}>
              {canDefer
                ? `${decision.remainingDeferrals} skip${decision.remainingDeferrals === 1 ? '' : 's'} remaining`
                : 'Action required'}
            </Badge>
          </div>
          <DialogTitle>
            {isPersonalGate ? 'Complete your personal details' : 'Finish setting up your profile'}
          </DialogTitle>
          <DialogDescription>
            {isPersonalGate
              ? 'Your core identity and contact details are required before you can use the workspace.'
              : canDefer
                ? 'WorkLynx needs the remaining information for rostering, safety, payroll and compliance. You may finish it now or defer until your next sign-in.'
                : 'You have used both setup deferrals. Complete the remaining required information to continue.'}
          </DialogDescription>
        </DialogHeader>

        <div className="px-5 pb-5">
          <div className="rounded-md border border-brand-border/30 bg-brand-soft/25 p-4">
            <p className="text-xs font-semibold uppercase text-muted">Still required</p>
            <div className="mt-3 grid gap-2 sm:grid-cols-2">
              {visibleIssues.map((issue) => (
                <div key={issue.id} className="flex min-w-0 items-center gap-2 text-sm">
                  <WorklynxIcon name="success" size={16} className="shrink-0 text-muted/70" />
                  <span className="truncate">{issue.label}</span>
                </div>
              ))}
            </div>
            {summary.issues.length > visibleIssues.length ? (
              <p className="mt-3 text-xs text-muted">
                +{summary.issues.length - visibleIssues.length} more item
                {summary.issues.length - visibleIssues.length === 1 ? '' : 's'}
              </p>
            ) : null}
          </div>

          {decision.section ? (
            <p className="mt-3 text-sm text-muted">
              Start with <span className="font-semibold text-foreground">{SECTION_LABELS[decision.section]}</span>.
            </p>
          ) : null}

          {deferError ? (
            <p className="mt-3 rounded-md bg-danger-soft px-3 py-2 text-sm text-danger" role="alert">
              We could not save your deferral. Please try again or complete the required fields now.
            </p>
          ) : null}
        </div>

        <DialogFooter>
          {canDefer ? (
            <Button variant="outline" disabled={isDeferring} onClick={() => void onDefer()}>
              {isDeferring ? 'Saving...' : 'Skip until next sign-in'}
            </Button>
          ) : null}
          <Button onClick={() => navigate(sectionPath)}>
            Review missing fields
            <WorklynxIcon name="arrow-right" size={16} />
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
